import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _descriptor, _descriptor2;
/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { DRAG_DROP_TRANSFER_KEY } from "abreact/incubator/ui/abBooleanLogicTree/AbBooleanLogicTreeDragDrop";
import Immutable from "immutable";
import { action, computed, observable, runInAction, makeObservable } from "mobx";
export let AbBooleanLogicTreeDragDropMachine = (_class = class AbBooleanLogicTreeDragDropMachine {
  constructor() {
    _initializerDefineProperty(this, "_currentDropTargetId", _descriptor, this);
    _initializerDefineProperty(this, "_currentDragStartKey", _descriptor2, this);
    _defineProperty(this, "_currentDropTargetConditionKey", undefined);
    _defineProperty(this, "_currentDropTargetPosition", undefined);
    _defineProperty(this, "_dropTargetIdCounter", void 0);
    _defineProperty(this, "_anchorRef", void 0);
    makeObservable(this);
    this._currentDropTargetId = undefined;
    this._currentDragStartKey = undefined;
    this._dropTargetIdCounter = 1;
  }
  generateDropTargetId() {
    return `dropTarget-${this._dropTargetIdCounter++}`;
  }
  isValidDropCandidate(e) {
    if (e.dataTransfer && e.dataTransfer.types.includes(DRAG_DROP_TRANSFER_KEY)) {
      const closestDropAffordanceId = this.findClosestDropAffordance(e.clientY)?.getAttribute("data-drop-target-id") ?? undefined;
      if (closestDropAffordanceId !== undefined && this.isNonAdjacentDropAffordance(closestDropAffordanceId)) {
        return true;
      }
    }
    return false;
  }
  get currentDropTargetId() {
    return this._currentDropTargetId;
  }
  get currentDragStartKey() {
    return this._currentDragStartKey;
  }
  set currentDragStartKey(currentDragStartKey) {
    runInAction(() => {
      this._currentDragStartKey = currentDragStartKey;
    });
  }
  set anchorRef(refHolder) {
    this._anchorRef = refHolder.current;
  }
  endDrag() {
    this.currentDragStartKey = undefined;
    this.clearCurrentDropTarget();
  }
  startDrag(conditionKey) {
    this.currentDragStartKey = conditionKey;
  }
  clearCurrentDropTarget() {
    this._currentDropTargetId = undefined;
    this._currentDropTargetPosition = undefined;
    this._currentDropTargetConditionKey = undefined;
  }
  onDragOver(e) {
    let computedDropTarget = this.computeDropTarget(e);
    runInAction(() => {
      if (computedDropTarget !== undefined) {
        this._currentDropTargetId = computedDropTarget.dropTargetId;
        this._currentDropTargetPosition = computedDropTarget.position;
        this._currentDropTargetConditionKey = computedDropTarget.conditionKey;
      } else {
        this._currentDropTargetId = undefined;
        this._currentDropTargetPosition = undefined;
        this._currentDropTargetConditionKey = undefined;
      }
    });
  }
  findClosestDropAffordance(targetY) {
    if (this._anchorRef === null || this._anchorRef === undefined) return undefined;
    const dropTargets = this._anchorRef.querySelectorAll(".ab-booltree-drop-affordance");
    let closestTarget = undefined;
    let minDiff = undefined;
    for (let j = 0; j < dropTargets.length; j++) {
      const currentDropTarget = dropTargets[j];
      const {
        y: rowTop,
        height
      } = currentDropTarget.getBoundingClientRect();
      const targetMedianY = rowTop - height / 2;
      const diff = Math.abs(targetMedianY - targetY);
      if (minDiff === undefined || diff < minDiff) {
        minDiff = diff;
        closestTarget = currentDropTarget;
      }
    }
    return closestTarget;
  }

  /** Use key information stored in the drag affordance to compute the intended target for drop **/
  computeDropTarget(e) {
    const closestTarget = this.findClosestDropAffordance(e.clientY);
    if (closestTarget === undefined) {
      return;
    } else {
      const targetRowKey = closestTarget.getAttribute("data-affordance-associated-key") ?? undefined;
      const dragDropId = closestTarget.getAttribute("data-drop-target-id") ?? undefined;
      const direction = closestTarget.classList.contains("header-row-drop-affordance") ? "above" : "below";
      if (targetRowKey === undefined || dragDropId === undefined || this.isNonAdjacentDropAffordance(dragDropId) === false) {
        return;
      }
      return {
        dropTargetId: dragDropId,
        position: direction,
        conditionKey: targetRowKey
      };
    }
  }
  getDragConditionKey(e) {
    if (!this.isValidDropCandidate(e)) {
      return null;
    }
    const conditionKey = e.dataTransfer.getData(DRAG_DROP_TRANSFER_KEY);
    return conditionKey === "" ? null : conditionKey;
  }
  getDropTargetInfo() {
    if (this._currentDropTargetConditionKey) {
      return {
        conditionKey: this._currentDropTargetConditionKey,
        dropPosition: this._currentDropTargetPosition
      };
    } else {
      return null;
    }
  }
  isNonAdjacentDropAffordance(affordanceId) {
    const originKey = this._currentDragStartKey;
    if (originKey) return this.findAdjacentConditionAffordances(originKey)?.contains(affordanceId) === false;
    return true;
  }

  /** Use key information stored in the drag affordance to compute the intended target for drop **/
  findAdjacentConditionAffordances(conditionKey) {
    if (this._anchorRef === null || this._anchorRef === undefined) return undefined;
    const conditionElements = this._anchorRef.querySelectorAll(".ab-booltree-condition-row");
    let originCondition;
    for (let j = 0; j < conditionElements.length; j++) {
      if (conditionElements[j].getAttribute("data-condition-key") === conditionKey) {
        originCondition = conditionElements[j];
      }
    }
    if (originCondition === undefined) return undefined;
    const dropTargets = this._anchorRef.querySelectorAll(".ab-booltree-drop-affordance");
    const {
      y: rowTop,
      height
    } = originCondition.getBoundingClientRect();
    const originY = rowTop + height / 2;

    // Find the closest Affordance above & below originCondition
    let minDiffAbove = Math.min();
    let minDiffBelow = Math.max(); //React Y axis is inverted
    let closestAbove;
    let closestBelow;
    for (let j = 0; j < dropTargets.length; j++) {
      const currentDropTarget = dropTargets[j];
      const {
        y: rowTop,
        height
      } = currentDropTarget.getBoundingClientRect();
      const targetMedianY = rowTop + height / 2;
      const diff = originY - targetMedianY; // Positive diff is Above, Negative diff is Below
      if (diff < 0) {
        // Process Below
        if (diff > minDiffBelow) {
          minDiffBelow = diff;
          closestBelow = currentDropTarget;
        }
      } else if (diff >= 0) {
        //Process Above
        if (diff < minDiffAbove) {
          minDiffAbove = diff;
          closestAbove = currentDropTarget;
        }
      }
    }
    let closestAboveKey = closestAbove?.getAttribute("data-drop-target-id") ?? undefined;
    let closestBelowKey = closestBelow?.getAttribute("data-drop-target-id") ?? undefined;
    if (closestAboveKey && closestBelowKey) return Immutable.List([closestAboveKey, closestBelowKey]);
    return undefined;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "_currentDropTargetId", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "_currentDragStartKey", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "currentDropTargetId", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "currentDropTargetId"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "currentDragStartKey", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "currentDragStartKey"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "endDrag", [action], Object.getOwnPropertyDescriptor(_class.prototype, "endDrag"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "startDrag", [action], Object.getOwnPropertyDescriptor(_class.prototype, "startDrag"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "clearCurrentDropTarget", [action], Object.getOwnPropertyDescriptor(_class.prototype, "clearCurrentDropTarget"), _class.prototype), _class);
//# sourceMappingURL=AbBooleanLogicTreeDragDropMachine.js.map
